import React, { useState } from 'react';
import { CustomPrompt, CustomPromptProps, emptyCustomPromptPropsWithExitConfirmation } from '../../widgets/components/ModalPrompts';
import { upsertBizSettings } from '../../../api'; // Add this import
import { toast } from 'react-toastify'; // Add this import
import { BizSettings } from '../../../types'; // Add this import
import { useAppContext } from '../../apps/AppContext';

interface TaxSettingsProps {
    onUpdate: (updatedTaxFlag: boolean) => void;
}

const TaxSettingsButton: React.FC<TaxSettingsProps> = ({ onUpdate }) => {

    const [promptProps, setPromptProps] = useState<CustomPromptProps>(emptyCustomPromptPropsWithExitConfirmation);
    const [loading, setLoading] = useState(false);
    
    const {bizSettings,setBizSettings} = useAppContext()
    const isEnabled = bizSettings?.taxFlag !== false;
    const handleTaxToggle = () => {
        if (isEnabled) {
            // Confirm disabling tax
            setPromptProps({
                ...emptyCustomPromptPropsWithExitConfirmation,
                isVisible: true,
                title: 'Disable Tax Features'.toTranslatedDisplay(),
                message: (
                    <div>
                        <p>{'Are you sure you want to disable tax features?'.toTranslatedDisplay()}</p>
                        <p className="text-warning">
                            <strong>{'Warning:'.toTranslatedDisplay()}</strong> {'This action will:'.toTranslatedDisplay()}
                        </p>
                        <ul className="text-muted">
                            <li>{'Remove tax fields from forms'.toTranslatedDisplay()}</li>
                            <li>{'Hide tax columns in reports'.toTranslatedDisplay()}</li>
                            <li>{'Disable tax calculations'.toTranslatedDisplay()}</li>
                            <li>{'Hide tax-related features throughout the application'.toTranslatedDisplay()}</li>
                        </ul>
                    </div>
                ),
                PrimaryText: 'Disable Tax'.toTranslatedDisplay(),
                secondaryText: 'Cancel'.toTranslatedDisplay(),
                onConfirm:  async () => {
                    try {
                        await updateTaxSettings(false);
                    } finally {
                        setPromptProps(emptyCustomPromptPropsWithExitConfirmation);
                    }
                },
                onClose: () => {
                    setPromptProps(emptyCustomPromptPropsWithExitConfirmation);
                }
            });
        } else {
            // Confirm enabling tax
            setPromptProps({
                ...emptyCustomPromptPropsWithExitConfirmation,
                isVisible: true,
                title: 'Enable Tax Features'.toTranslatedDisplay(),
                message: (
                    <div>
                        <p>{'Enable tax features for your business?'.toTranslatedDisplay()}</p>
                        <p className="text-info">
                            <strong>{'This will:'.toTranslatedDisplay()}</strong>
                        </p>
                        <ul className="text-muted">
                            <li>{'Add tax fields to invoices and transactions'.toTranslatedDisplay()}</li>
                            <li>{'Show tax columns in reports'.toTranslatedDisplay()}</li>
                            <li>{'Enable tax calculations and reporting'.toTranslatedDisplay()}</li>
                            <li>{'Activate tax-related features throughout the application'.toTranslatedDisplay()}</li>
                        </ul>
                    </div>
                ),
                PrimaryText: 'Enable Tax'.toTranslatedDisplay(),
                secondaryText: 'Cancel'.toTranslatedDisplay(),
                onConfirm: 
                
                async () => {
                    try {
                        await updateTaxSettings(true);
                    } finally {
                        setPromptProps(emptyCustomPromptPropsWithExitConfirmation);
                    }
                },
                onClose: () => {
                    setPromptProps(emptyCustomPromptPropsWithExitConfirmation);
                }
            });
        }
    };

    const updateTaxSettings = async (newTaxFlag: boolean) => {
        try {
            setLoading(true);
            
            // Update the bizSettings with the new tax flag
            const updatedSettings: BizSettings = {
                ...bizSettings,
                taxFlag: newTaxFlag
            };

            // Call the API to update settings
            const response = await upsertBizSettings(updatedSettings);
            setBizSettings(updatedSettings)
            // Update local state through parent component with the API response
            // This ensures we use the actual saved value from the server
            onUpdate(response?.taxFlag !== undefined ? response.taxFlag : newTaxFlag);
            
            // Show success message
            toast.success(`${'Tax features'.toTranslatedDisplay()} ${(newTaxFlag ? 'enabled' : 'disabled').toTranslatedDisplay()} ${'successfully'.toTranslatedDisplay()}`);
            
        } catch (error) {
            console.error('Failed to update tax settings:', error);
            toast.error('Failed to update tax settings. Please try again.'.toTranslatedDisplay());
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <button
                type="button"
                className={`btn btn-sm ${isEnabled ? 'btn-primary' : 'btn-danger'}`}
                onClick={handleTaxToggle}
                disabled={loading}
                title={(isEnabled ? 'Tax features are enabled' : 'Tax features are disabled').toTranslatedDisplay()}
            >
                {loading ? (
                    <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        {'Updating...'.toTranslatedDisplay()}
                    </>
                ) : (
                    <>
                        <i className={`fas ${isEnabled ? 'fa-percentage' : 'fa-ban'} me-1`}></i>
                        {(isEnabled ? 'Tax Enabled' : 'Tax Disabled').toTranslatedDisplay()}
                    </>
                )}
            </button>
            
            <CustomPrompt {...promptProps} />
        </>
    );
};

export default TaxSettingsButton;